import { useEffect, useRef, useState } from "react";
import { Check, ClipboardPaste, LoaderCircle } from "lucide-react";
import {
  formatPoolDenomination,
  type DepositState,
  type ProtocolPool,
  type WithdrawState,
} from "@zkresistor/shared";
import {
  ASSETS,
  SecretNote,
  Toggle,
  useController,
  type PrivateRuntime,
} from "@zkresistor/client";
import { PoolHero, PoolStats } from "../components/Pool";
import { Loading } from "../components/Status";

export type TransferMode = "deposit" | "withdraw";

interface PoolPageProps {
  runtime: PrivateRuntime;
  mode: TransferMode;
  pools: ProtocolPool[];
  selected: ProtocolPool;
  walletAddress: string;
  onMode: (mode: TransferMode, pool: ProtocolPool) => void;
}

export function PoolPage({ runtime, mode, pools, selected, walletAddress, onMode }: PoolPageProps) {
  const deposit = useController(runtime.deposit);
  const withdraw = useController(runtime.withdraw);
  const pool = mode === "deposit" && (deposit.status === "prepared" || deposit.status === "submitted")
    ? deposit.pool
    : mode === "withdraw" && (withdraw.status === "prepared" || withdraw.status === "submitted")
      ? withdraw.pool
      : selected;

  return (
    <section className="pool-flow">
      <PoolHero pool={pool} />
      <div className="transfer-tabs" role="tablist" aria-label="Transfer mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "deposit"}
          className={mode === "deposit" ? "active" : ""}
          onClick={() => onMode("deposit", pool)}
        >
          <span className="deposit-action-icon" aria-hidden="true" />
          Deposit
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "withdraw"}
          className={mode === "withdraw" ? "active" : ""}
          onClick={() => onMode("withdraw", pool)}
        >
          <span className="withdraw-action-icon" aria-hidden="true" />
          Withdraw
        </button>
      </div>
      <h2 className="section-title">Pool</h2>
      <PoolStats pool={pool} />
      {mode === "deposit"
        ? <Deposit pool={pool} state={deposit} walletAddress={walletAddress} controller={runtime.deposit} />
        : <Withdraw pools={pools} state={withdraw} walletAddress={walletAddress} controller={runtime.withdraw} />}
    </section>
  );
}

interface DepositProps {
  pool: ProtocolPool;
  state: DepositState;
  walletAddress: string;
  controller: PrivateRuntime["deposit"];
}

function Deposit({ pool, state, walletAddress, controller }: DepositProps) {
  const lastPrepared = useRef<Extract<DepositState, { status: "prepared" | "submitted" }> | null>(null);
  if (state.status === "prepared" || state.status === "submitted") lastPrepared.current = state;
  if (state.status === "idle" || state.status === "error") lastPrepared.current = null;
  const submitting = state.status === "working" && lastPrepared.current !== null;
  const displayed = state.status === "prepared" || state.status === "submitted"
    ? state
    : submitting ? lastPrepared.current : null;

  return (
    <div className="transfer-body">
      <div className="amount-band">
        <span>Deposit amount</span>
        <strong>{formatPoolDenomination(pool)} {pool.symbol}</strong>
      </div>
      {state.status === "working" && !submitting && <Loading label={state.phase} />}
      {state.status === "error" && <p className="alert error">{state.error}</p>}
      {displayed === null && state.status !== "working" && (
        <button
          className="primary"
          type="button"
          disabled={walletAddress === ""}
          onClick={() => void controller.prepare(pool, walletAddress)}
        >
          Generate Secret
        </button>
      )}
      {displayed !== null && (
        <div className="note-stage">
          <div className="note-head">
            <span>Secret note</span>
            <span className="memory-badge">Click to copy</span>
          </div>
          <SecretNote value={displayed.note} />
          {state.status === "prepared" && (
            <>
              <div className="toggle-row">
                <label className="toggle-row-label" htmlFor="confirm-saved">I saved this note</label>
                <Toggle
                  id="confirm-saved"
                  on={state.confirmed}
                  onChange={(value) => controller.confirmSaved(value)}
                  ariaLabel="I saved this note"
                />
              </div>
              <button
                className="primary"
                type="button"
                disabled={!state.confirmed}
                onClick={() => void controller.submit()}
              >
                <span className="deposit-action-icon" aria-hidden="true" />
                Deposit
              </button>
            </>
          )}
          {submitting && (
            <button className="primary status-progress" type="button" disabled aria-live="polite" aria-busy="true">
              <LoaderCircle size={18} />
              Submitting…
            </button>
          )}
          {state.status === "submitted" && (
            <button className="primary status-submitted" type="button" disabled aria-live="polite">
              <Check size={18} />
              Deposit submitted
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface WithdrawProps {
  pools: ProtocolPool[];
  state: WithdrawState;
  walletAddress: string;
  controller: PrivateRuntime["withdraw"];
}

function Withdraw({ pools, state, walletAddress, controller }: WithdrawProps) {
  const [note, setNote] = useState("");
  const [recipient, setRecipient] = useState("");
  const [pasted, setPasted] = useState(false);
  const [useRelayer, setUseRelayer] = useState(true);
  const lastPrepared = useRef<Extract<WithdrawState, { status: "prepared" | "submitted" }> | null>(null);
  if (state.status === "prepared" || state.status === "submitted") lastPrepared.current = state;
  if (state.status === "idle" || state.status === "error") lastPrepared.current = null;
  const submitting = state.status === "working" && lastPrepared.current !== null;
  const lockedRecipient = state.status === "prepared" || state.status === "submitted"
    ? state.recipient
    : submitting ? lastPrepared.current?.recipient ?? null : null;

  useEffect(() => {
    if (!pasted) return;
    const timeout = window.setTimeout(() => setPasted(false), 1_600);
    return () => window.clearTimeout(timeout);
  }, [pasted]);

  useEffect(() => {
    if (state.status === "prepared") setNote("");
  }, [state.status]);

  const pasteNote = () => {
    void navigator.clipboard.readText().then((value) => {
      setNote(value);
      setPasted(true);
    }).catch(() => undefined);
  };

  return (
    <div className="transfer-body">
      <div className="form-stack">
        <div className="field">
          <div className="field-head">
            <label htmlFor="withdraw-note">Secret note</label>
            <button className={`paste-button${pasted ? " pasted" : ""}`} type="button" onClick={pasteNote}>
              {pasted ? <Check size={15} /> : <ClipboardPaste size={15} />}
              <span>{pasted ? "Pasted" : "Paste"}</span>
            </button>
          </div>
          <textarea
            id="withdraw-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        <label className="field">
          Recipient
          <div className={`recipient-control${lockedRecipient !== null ? " locked" : ""}`}>
            <input
              value={lockedRecipient ?? recipient}
              onChange={(event) => setRecipient(event.target.value)}
              placeholder="Defaults to connected wallet"
              spellCheck={false}
              autoComplete="off"
              readOnly={lockedRecipient !== null}
              aria-readonly={lockedRecipient !== null}
            />
            {lockedRecipient !== null && <img className="recipient-lock" src={ASSETS.lock} alt="" aria-hidden="true" />}
          </div>
        </label>
      </div>

      {state.status !== "submitted" && !submitting && (
        <div className="toggle-row">
          <label className="toggle-row-label" htmlFor="use-relayer">Use relayer</label>
          <Toggle id="use-relayer" on={useRelayer} onChange={setUseRelayer} ariaLabel="Use relayer" />
        </div>
      )}
      {state.status === "working" && !submitting && <Loading label={state.phase} />}
      {state.status === "error" && <p className="alert error">{state.error}</p>}
      {(state.status === "idle" || state.status === "error") && (
        <button
          className="primary"
          type="button"
          disabled={!note.trim() || (!recipient.trim() && !walletAddress) || pools.length === 0}
          onClick={() => void controller.prepare(note, recipient.trim() || walletAddress, pools)}
        >
          Generate Proof
        </button>
      )}
      {state.status === "prepared" && (
        <div className="review">
          {state.relayError !== undefined && <p className="alert error">{state.relayError}</p>}
          <button
            className="primary"
            type="button"
            disabled={!useRelayer && !walletAddress}
            onClick={() => void (useRelayer ? controller.submit() : controller.submitWithWallet())}
          >
            <span className="withdraw-action-icon" aria-hidden="true" />
            {useRelayer ? "Withdraw via relayer" : "Withdraw with wallet"}
          </button>
        </div>
      )}
      {submitting && (
        <button className="primary status-progress" type="button" disabled aria-live="polite" aria-busy="true">
          <LoaderCircle size={18} />
          Submitting…
        </button>
      )}
      {state.status === "submitted" && (
        <button
          className={`primary ${state.confirmed ? "status-confirmed" : "status-submitted"}`}
          type="button"
          disabled
          aria-live="polite"
        >
          <Check size={18} />
          {state.confirmed ? "Withdrawal confirmed" : "Withdrawal submitted"}
        </button>
      )}
    </div>
  );
}
